import { useEffect, useState } from "react";
import { LayoutDashboard, Package, UtensilsCrossed, Truck, BarChart3, Building2, CreditCard, ReceiptText, Bell, Plus, Search, ChevronDown, ArrowUpRight, AlertTriangle, Check, Clock3, CircleDollarSign, Menu, X, LogOut, ShoppingCart, Lock, Pencil, MonitorPlay, History, Users, Trophy, MessageCircle, Contact, BookOpen, BedDouble, PartyPopper, Wallet, Scale } from "lucide-react";
import { api, money, errorMessage } from "./api";
import { useAuth } from "./auth/AuthContext";
import POSView from "./views/POS";
import BillView from "./views/Bill";
import RecipesView from "./views/Recipes";
import MenuView from "./views/Menu";
import TablesView from "./views/Tables";
import KitchenView from "./views/Kitchen";
import StaffView from "./views/Staff";
import KotHistoryView from "./views/KotHistory";
import InsightsView from "./views/Insights";
import DigestView from "./views/Digest";
import BillingView from "./views/Billing";
import CustomersView from "./views/Customers";
import InventoryView from "./views/Inventory";
import PurchasingView from "./views/Purchasing";
import SuppliersView from "./views/Suppliers";
import HotelView from "./views/Hotel";
import HallsView from "./views/Halls";
import ExpensesView from "./views/Expenses";
import PnlView from "./views/Pnl";
import SalesReportView from "./views/SalesReport";
import Plans from "./plans/Plans";
import { useSmartFields } from "./lib/smartfields";
import { fmtD } from "./lib/units";
import { useOvernight, OvernightBanner } from "./components/OvernightBanner";
import "@/App.css";
import "@/india.css";
import "@/plans/plans.css";

export const GROUPS = {
  pos: { label: "POS & Sales", tabs: [["pos", "Order"], ["tables", "Floor / KOT Status"], ["billing", "Final Bill"]] },
  menu: { label: "Menu & Pricing", tabs: [["menu", "Menu Items & Pricing"], ["recipes", "Recipes & Costing"]] },
};
const groupOf = (pg) => (["pos", "tables", "billing"].includes(pg) ? "pos" : ["menu", "recipes"].includes(pg) ? "menu" : pg);

function SubTabs({ group, page, setPage, pages, branch }) {
  const [ready, setReady] = useState(0);
  useEffect(() => {
    if (group !== "pos") return undefined;
    const f = () => api.get(`/tables?branch=${branch}`).then(({ data }) => setReady(data.filter((t) => t.tab_summary?.ready_to_bill).length)).catch(() => {});
    f(); const iv = setInterval(f, 15000); return () => clearInterval(iv);
  }, [group, branch, page]);
  const tabs = GROUPS[group].tabs.filter(([id]) => !pages || pages.includes(id));
  if (tabs.length < 2) return null;
  return (
    <div className="ins-toggle sub-tabs" data-testid={`${group}-subtabs`}>
      {tabs.map(([id, label]) => (
        <button key={id} className={page === id ? "on" : ""} onClick={() => setPage(id)} data-testid={`subtab-${id}`}>
          {label}{id === "billing" && ready > 0 && <i className="sub-count" data-testid="subtab-billing-count">{ready}</i>}
        </button>
      ))}
    </div>
  );
}

const PAGE_FEATURE = { hotel: "hotel.rooms", halls: "hotel.halls", expenses: "accounts.pnl", pnl: "accounts.pnl" };
const PAGE_SUB = {
  kitchen: "Live tickets for the pass — closed bills and earlier days drop off automatically.", reports: "Sales, tickets, waiters and item margins for any date range.",
  insights: "Trends and outliers worth acting on.", digest: "Your end-of-day summary, ready to share.", purchasing: "Raise, receive and track purchase orders.",
  suppliers: "Supplier balances, payments and ledger history.", hotel: "Room board, reservations, folios and occupancy.", halls: "Banquet and event bookings with advances and folios.",
  expenses: "Record every outgoing so P&L stays honest.", pnl: "Departmental and consolidated profit, GST and advances excluded.",
  "kot-history": "Every KOT and change, order by order.", subscription: "Your plan, renewals and GST invoices.", staff: "Team logins, roles and branch access.",
  branches: "Outlets, GST details and modules.", tables: "Floor plan, sections and waiters.", menu: "Menu items, prices and availability.",
};
const nav = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "pos", label: "POS & Sales", icon: ShoppingCart },
  { id: "kitchen", label: "Kitchen Display", icon: MonitorPlay },
  { id: "customers", label: "Customers", icon: Contact },
  { id: "inventory", label: "Inventory & Stock", icon: Package },
  { id: "menu", label: "Menu & Pricing", icon: UtensilsCrossed },
  { id: "purchasing", label: "Purchasing", icon: Truck },
  { id: "suppliers", label: "Suppliers & Ledger", icon: BookOpen },
  { id: "hotel", label: "Rooms & Bookings", icon: BedDouble, hotel: true },
  { id: "halls", label: "Hall Bookings", icon: PartyPopper, hotel: true },
  { id: "expenses", label: "Expenses", icon: Wallet },
  { id: "pnl", label: "Profit & Loss", icon: Scale },
  { id: "reports", label: "Sales & Reports", icon: BarChart3 },
  { id: "insights", label: "Performance Insights", icon: Trophy },
  { id: "digest", label: "Daily Digest", icon: MessageCircle },
  { id: "bills", label: "Customer Bills", icon: ReceiptText },
  { id: "kot-history", label: "KOT History", icon: History },
  { id: "staff", label: "Staff & Roles", icon: Users },
  { id: "branches", label: "Branch Settings", icon: Building2 },
  { id: "subscription", label: "Subscription", icon: CreditCard },
];

function Metric({ label, value, detail, icon: Icon, tone = "orange" }) {
  return (
    <div className="metric" data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className={`metric-icon ${tone}`}><Icon size={18} /></div>
      <div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
    </div>
  );
}

function BarChart({ points }) {
  const max = Math.max(30, ...points.map((p) => p.food_cost_pct || 0));
  return (
    <div className="chart-bars" data-testid="food-cost-chart">
      {points.map((p) => (
        <div className="bar-col" key={p.date}>
          <div className="bar" style={{ height: `${((p.food_cost_pct || 0) / max) * 100}%` }}><b>{p.food_cost_pct == null ? "—" : `${p.food_cost_pct}%`}</b></div>
          <small>{p.label}</small>
        </div>
      ))}
    </div>
  );
}

export default function KitchenShell() {
  const { user, outlet, logout } = useAuth();
  const isStaff = Array.isArray(user?.pages);
  const [page, setPage] = useState(isStaff ? user.pages[0] : "dashboard");
  const [branch, setBranch] = useState(isStaff ? user.branch_id : "");
  useSmartFields(branch);
  const overnight = useOvernight(branch);
  const overnightBanner = <OvernightBanner data={overnight} onOpen={() => setPage("tables")} />;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const modals = document.querySelectorAll(".modal-backdrop");
      modals[modals.length - 1]?.querySelector(".modal-head .icon-button, [data-testid^='close-']")?.click();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const [branches, setBranches] = useState([]);
  const hotelOn = !!branches.find((b) => b.id === branch)?.hotel_enabled;
  const allowedNav = (isStaff ? nav.filter((n) => user.pages.includes(n.id)) : nav).filter((n) => !n.hotel || hotelOn);
  const [data, setData] = useState({});
  const [inventory, setInventory] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [reports, setReports] = useState({ sales: [], items: [] });
  const [notice, setNotice] = useState("");
  const [mobile, setMobile] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [pendingDigest, setPendingDigest] = useState(null);
  const [posTable, setPosTable] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);

  const currentBranch = branches.find((b) => b.id === branch) || { name: outlet?.display_name || "Your Kitchen", city: outlet?.city || "", initials: (outlet?.display_name || "FD").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase() };

  const hasFeature = (key) => (entitlements?.features || []).includes(key);
  const trialExpired = entitlements?.trial_expired;

  const loadEntitlements = async () => {
    try { const { data } = await api.get("/me/entitlements"); setEntitlements(data); } catch { /* noop */ }
  };

  const load = async () => {
    try {
      const can = (pg) => !isStaff || user.pages.includes(pg);
      const skip = Promise.resolve({ data: null });
      const b = await api.get(`/branches`);
      const scoped = isStaff ? user.branch_id : branch;
      if (isStaff && branch !== scoped) { setBranches(b.data); setBranch(scoped); return; }
      if (!isStaff && !b.data.find((x) => x.id === branch)) { setBranches(b.data); if (b.data.length) setBranch(b.data[0].id); return; }
      const [d, i, r, rep] = await Promise.all([
        can("dashboard") ? api.get(`/dashboard?branch=${branch}`) : skip,
        can("inventory") || can("recipes") ? api.get(`/inventory?branch=${branch}`) : skip,
        can("tables") || can("pos") || can("recipes") ? api.get(`/recipes?branch=${branch}`) : skip,
        can("reports") ? api.get(`/reports?branch=${branch}`) : skip,
      ]);
      if (!isStaff || user.role === "manager") api.get(`/stock-alerts?branch=${branch}`).then(({ data: al }) => setStockAlerts(al)).catch(() => setStockAlerts([]));
      if (!isStaff) api.get(`/digests?branch=${branch}`).then(({ data: dg }) => setPendingDigest(dg[0] && !(dg[0].sent_log || []).length && (dg[0].metrics?.totals?.sales || 0) > 0 && (Date.now() - new Date(dg[0].date).getTime()) < 3 * 864e5 ? dg[0] : null)).catch(() => setPendingDigest(null));
      setBranches(b.data); setData(d.data || {}); setInventory(i.data || []); setRecipes(r.data || []); setReports(rep.data || { sales: [], items: [] });
    } catch (e) { setNotice(errorMessage(e) || "Could not connect to the FynDine workspace."); }
  };
  useEffect(() => { load(); loadEntitlements(); }, [branch]); // eslint-disable-line

  const activeGroup = groupOf(page);
  const activeLabel = GROUPS[activeGroup]?.label || nav.find((n) => n.id === page)?.label || "Dashboard";
  const trialDays = entitlements?.trial_days_remaining ?? (user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : 7);

  // Trial expired → force the Subscription view and block editing elsewhere
  if (!isStaff && trialExpired && page !== "subscription" && page !== "branches") {
    setPage("subscription");
  }

  const pageContent = () => {
    if (page === "pos") return <POSView branch={branch} outlet={outlet} recipes={recipes} initialTable={posTable} onInitialUsed={() => setPosTable(null)} onCheckout={(src) => { setCheckout(src); setPage("billing"); }} />;
    if (page === "billing") return <BillingView branch={branch} initial={checkout} onConsumed={() => setCheckout(null)} onBillCreated={load} />;
    if (page === "customers") return <CustomersView branch={branch} />;
    if (page === "tables") return <>{overnightBanner}<TablesView branch={branch} recipes={recipes} onGoPos={(t) => { setPosTable(t); setPage("pos"); }} onGoBill={(tab) => { setCheckout({ tab_id: tab.id }); setPage("billing"); }} /></>;
    if (page === "kitchen") return <KitchenView branch={branch} branchName={`${currentBranch.name}${currentBranch.city ? " · " + currentBranch.city : ""}`} allowed={!entitlements || hasFeature("kot.kds")} canLink={!isStaff || user.role === "manager"} onUpgrade={() => setPage("subscription")} />;
    if (isStaff && !user.pages.includes(page)) return <div className="panel" data-testid="role-blocked">This section isn't available for your role.</div>;
    const need = PAGE_FEATURE[page];
    if (need && entitlements && !hasFeature(need)) return (
      <div className="panel empty" data-testid="plan-locked">
        <p>{need.startsWith("hotel.") && entitlements.edition !== "hotel" ? "This needs a Restaurant + Hotel plan." : `This isn't included in your ${entitlements.plan_name || "current"} plan.`} Your existing data is safe and will reappear when you upgrade.</p>
        {!isStaff && <button className="primary-button" onClick={() => setPage("subscription")} data-testid="plan-locked-upgrade">See plans</button>}
      </div>
    );
    if (page === "staff") return <StaffView branches={branches} />;
    if (page === "digest") return <DigestView branch={branch} allowed={!entitlements || hasFeature("comm.daily_digest")} onUpgrade={() => setPage("subscription")} />;
    if (page === "insights") return <InsightsView branch={branch} allowed={!entitlements || hasFeature("reports.advanced")} onUpgrade={() => setPage("subscription")} />;
    if (page === "kot-history") return <KotHistoryView branch={branch} />;
    if (page === "inventory") return <>{stockAlerts.length > 0 && <div className="panel stock-alerts" data-testid="stock-alerts-panel"><div className="panel-head"><div><span className="overline">AUTO-DEPLETION ALERTS</span><h2>Below reorder level</h2></div></div>{stockAlerts.map((a) => <div className="stock-alert-row" key={a.id} data-testid={`stock-alert-${a.ingredient_id}`}><AlertTriangle size={14} /><b>{a.name}</b><span>{a.stock} {a.unit} left · reorder at {a.reorder} {a.unit}</span><button className="secondary-button" onClick={() => api.post(`/stock-alerts/${a.id}/ack`).then(() => setStockAlerts((x) => x.filter((y) => y.id !== a.id)))} data-testid={`stock-alert-ack-${a.ingredient_id}`}>Acknowledge</button></div>)}</div>}<InventoryView branch={branch} onChanged={load} /></>;
    if (page === "recipes") return <RecipesView recipes={recipes} inventory={inventory} branch={branch} onChanged={load} />;
    if (page === "purchasing") return <PurchasingView branch={branch} onChanged={load} />;
    if (page === "suppliers") return <SuppliersView branch={branch} branches={branches} />;
    if (page === "hotel") return hotelOn ? <HotelView branch={branch} /> : <div className="empty">Turn on the Hotel module for this branch in Branch Settings.</div>;
    if (page === "halls") return hotelOn ? <HallsView branch={branch} /> : <div className="empty">Turn on the Hotel module for this branch in Branch Settings.</div>;
    if (page === "expenses") return <ExpensesView branch={branch} branches={branches} />;
    if (page === "pnl") return <PnlView branches={branches} isOwner={!isStaff} />;
    if (page === "reports") return <SalesReportView branch={branch} />;
    if (page === "subscription") return <Plans entitlements={entitlements} trialExpired={trialExpired} onRefresh={loadEntitlements} onActivated={async () => { await loadEntitlements(); setPage("dashboard"); setNotice("Subscription activated. Welcome aboard!"); }} />;
    if (page === "branches") return <Branches branches={branches} current={branch} setBranch={setBranch} outlet={outlet} onAdded={load} />;
    if (page === "menu") return <MenuView recipes={recipes} inventory={inventory} branch={branch} onChanged={load} />;
    if (page === "bills") return <BillView branch={branch} outlet={outlet} lastBill={lastBill} setLastBill={setLastBill} />;
    return <>{overnightBanner}{stockAlerts.length > 0 && <div className="digest-banner stock" data-testid="stock-alert-banner"><AlertTriangle size={18} /><p><b>Low stock: {stockAlerts.length} item(s)</b> — {stockAlerts.slice(0, 4).map((a) => `${a.name} ${a.stock} ${a.unit}`).join(", ")}</p><button className="primary-button" onClick={() => setPage("inventory")} data-testid="stock-alert-open">View inventory</button></div>}{pendingDigest && <div className="digest-banner" data-testid="digest-banner"><MessageCircle size={18} /><p><b>Daily digest for {pendingDigest.date.split("-").reverse().join("-")} is ready.</b> Sales {money(pendingDigest.metrics.totals.sales)} · tap to send it on WhatsApp.</p><button className="primary-button" onClick={() => setPage("digest")} data-testid="digest-banner-open">Open digest</button></div>}<Dashboard data={data} recipes={recipes} setPage={setPage} entitlements={entitlements} /></>;
  };

  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <div className="brand-mark">{(outlet?.display_name || "F").charAt(0).toUpperCase()}</div>
          <div><b>{outlet?.display_name ? outlet.display_name.toUpperCase().slice(0, 18) : "FYNDINE"}</b><span>BY FYNDINE</span></div>
          <button className="icon-button close-mobile" onClick={() => setMobile(false)} data-testid="close-mobile-nav"><X size={18} /></button>
        </div>
        <nav>
          {allowedNav.map(({ id, label, icon: Icon }) => (
            <button key={id} className={groupOf(page) === id ? "nav-item active" : "nav-item"} onClick={() => { setPage(id); setMobile(false); }} data-testid={`nav-${id}`}>
              <Icon size={17} /><span>{label}</span>
              {id === "inventory" && data.low_stock > 0 && <em>{data.low_stock}</em>}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {!isStaff && <div className="trial-mini">
            <span>{entitlements?.source === "paid" ? "YOUR PLAN" : "TRIAL STATUS"}</span>
            <strong>{entitlements?.source === "paid" ? entitlements.plan_name : trialDays > 0 ? `${trialDays} days left` : "Trial ended"}</strong>
            {entitlements?.source !== "paid" && <div className="progress"><i style={{ width: `${Math.min(100, (trialDays / 7) * 100)}%` }} /></div>}
            <button onClick={() => setPage("subscription")} data-testid="sidebar-manage-trial">{entitlements?.source === "paid" ? "Manage plan" : "Choose a plan"} <ArrowUpRight size={14} /></button>
          </div>}
          <div className="user">
            <div className="avatar">{(user?.name || "U").slice(0, 2).toUpperCase()}</div>
            <div><b>{user?.name || "Owner"}{isStaff && <i className="role-chip" data-testid="sidebar-role-chip">{user.role.toUpperCase()}</i>}</b><span data-testid="sidebar-user-sub">{isStaff ? user.phone : (user?.email || "Owner account")}</span></div>
            <button className="icon-button" onClick={logout} data-testid="sidebar-logout" aria-label="Sign out"><LogOut size={15} /></button>
          </div>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <button className="mobile-menu icon-button" onClick={() => setMobile(true)} data-testid="open-mobile-nav"><Menu size={20} /></button>
          <div className="branch-switch">
            <span className="overline">ACTIVE BRANCH</span>
            <button onClick={() => !isStaff && setPage("branches")} data-testid="active-branch-switch">
              <div className="branch-avatar">{currentBranch.initials}</div>
              <div><b>{currentBranch.name}</b><small>{currentBranch.city}</small></div>
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="top-actions">
            <button className="icon-button" data-testid="notifications-button" onClick={() => (overnight?.tables || overnight?.stale_kots) ? setPage("tables") : pendingDigest && setPage("digest")} title={(overnight?.tables || overnight?.stale_kots) ? `${overnight.tables} table(s) left open overnight` : pendingDigest ? "Daily digest ready" : "No new notifications"}><Bell size={18} />{(pendingDigest || overnight?.tables > 0 || overnight?.stale_kots > 0) && <i data-testid="notifications-dot" />}</button>
            <div className="top-avatar">{(user?.name || "U").slice(0, 2).toUpperCase()}</div>
          </div>
        </header>
        <section className="content">
          <div className="page-head">
            <div>
              <span className="overline">{page === "dashboard" ? "OPERATIONS OVERVIEW" : "FYNDINE / " + activeLabel.toUpperCase()}</span>
              <h1>{page === "dashboard" ? `Good day, ${(user?.name || "Chef").split(" ")[0]}.` : activeLabel}</h1>
              <p>
                {page === "dashboard" ? "Here's the pulse of your kitchen across today's service." :
                 page === "inventory" ? "Keep every ingredient accounted for and every prep on track." :
                 page === "recipes" ? "Know what every plate costs before it leaves the pass." :
                 page === "pos" ? "Pick a table, punch in the order and send & print the KOT." :
                 page === "billing" ? "Capture customer, apply discount/service, redeem points and print the GST bill." :
                 page === "customers" ? "Customer registry by mobile — visits, spend and loyalty points." :
                 (PAGE_SUB[page] || "Everything for this outlet, in one place.")}
              </p>
            </div>
          </div>
          {notice && <div className="toast" data-testid="success-notice"><Check size={16} />{notice}<button onClick={() => setNotice("")}><X size={14} /></button></div>}
          {GROUPS[activeGroup] && <SubTabs group={activeGroup} page={page} setPage={setPage} pages={isStaff ? user.pages : null} branch={branch} />}
          {pageContent()}
        </section>
      </main>
    </div>
  );
}

function Dashboard({ data, recipes, setPage, entitlements }) {
  const trend = data.trend || [];
  return (
    <>
      <div className="metrics-grid">
        <Metric label="Food cost" value={data.food_cost == null ? "—" : `${data.food_cost}%`} detail={data.food_cost == null ? "No sales in the last 7 days" : "Last 7 days, from recipe costs"} icon={CircleDollarSign} />
        <Metric label="Low-stock alerts" value={data.low_stock || 0} detail={data.ingredient_count ? "Needs your attention" : "No ingredients yet"} icon={AlertTriangle} tone="red" />
        <Metric label="Today's revenue" value={money(data.revenue || 0)} detail={`${data.orders_count || 0} bills today`} icon={BarChart3} tone="green" />
        {entitlements?.source === "paid"
          ? <Metric label="Your plan" value={entitlements.plan_name} detail={entitlements.subscription?.ends_at ? `Paid until ${entitlements.subscription.ends_at.slice(0, 10).split("-").reverse().join("-")}` : "Active"} icon={Clock3} tone="slate" />
          : <Metric label="Trial remaining" value={`${data.trial_days ?? 7} days`} detail={data.trial_days === 0 ? "Trial ended" : "Full workspace access"} icon={Clock3} tone="slate" />}
      </div>
      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-head"><div><span className="overline">LAST 7 DAYS</span><h2>Food cost trend</h2></div></div>
          {trend.some((t) => t.food_cost_pct != null) ? <>
            <div className="chart-legend"><span><i className="legend-dot orange" />Actual food cost</span><span className="target-line" />Target &lt; 28%</div>
            <BarChart points={trend} />
          </> : <div className="empty" data-testid="food-cost-empty">No sales yet — the trend appears once bills are raised for dishes with costed recipes.</div>}
        </section>
        <section className="panel alerts-panel">
          <div className="panel-head"><div><span className="overline">ACTION NEEDED</span><h2>Stock alerts</h2></div><button className="text-button" onClick={() => setPage("inventory")} data-testid="view-inventory-alerts">View inventory <ArrowUpRight size={14} /></button></div>
          {(data.low_items || []).slice(0, 3).map((x, i) => (
            <div className="alert-row" key={x.id || i}>
              <div className="alert-icon"><AlertTriangle size={15} /></div>
              <div><b>{x.name}</b><span>{x.stock} {x.unit} remaining · reorder at {x.reorder} {x.unit}</span></div>
              <button className="outline-button" onClick={() => setPage("purchasing")} data-testid={`reorder-${i}`}>Reorder</button>
            </div>
          ))}
          {!data.low_stock && <div className="empty"><Check size={20} /> {data.ingredient_count ? "All stock levels look healthy" : "Add ingredients to track stock"}</div>}
        </section>
      </div>
      <section className="panel">
        <div className="panel-head"><div><span className="overline">RECIPE MASTER</span><h2>Top menu costing</h2></div><button className="text-button" onClick={() => setPage("recipes")} data-testid="view-all-recipes">View all recipes <ArrowUpRight size={14} /></button></div>
        {recipes.length === 0 ? <div className="empty" data-testid="dashboard-recipes-empty">No dishes yet. Create your first recipe under Menu &amp; Pricing.</div> :
        <div className="table-wrap"><table><thead><tr><th>Menu item</th><th>Category</th><th>Cost / serving</th><th>Price</th><th>Food cost</th></tr></thead><tbody>
          {recipes.slice(0, 4).map((r) => {
            const cps = r.cost_per_serving ?? r.cost / Math.max(1, r.yield_servings);
            const pct = r.selling_price > 0 ? cps / r.selling_price * 100 : null;
            return (
              <tr key={r.id}>
                <td><b>{r.name}</b><span>{r.ingredients_count || 0} ingredients · {r.wastage}% waste</span></td>
                <td><span className="pill neutral">{r.category}</span></td>
                <td>{money(cps)}</td>
                <td>{money(r.selling_price)}</td>
                <td>{pct == null ? "—" : <span className={`cost-chip ${pct < 30 ? "good" : "warn"}`}>{pct.toFixed(1)}%</span>}</td>
              </tr>
            );
          })}
        </tbody></table></div>}
      </section>
    </>
  );
}

function Reports({ reports }) {
  const sales = reports.sales || [];
  const max = Math.max(1, ...sales.map((s) => s.value));
  const has = (reports.bills || 0) > 0;
  return (
    <>
      <div className="metrics-grid">
        <Metric label="Weekly revenue" value={money(reports.total || 0)} detail={reports.change_pct == null ? "Last 7 days" : `${reports.change_pct >= 0 ? "↑" : "↓"} ${Math.abs(reports.change_pct)}% vs prior 7 days`} icon={BarChart3} tone="green" />
        <Metric label="Bills raised" value={reports.bills || 0} detail="Last 7 days, excl. cancelled" icon={UtensilsCrossed} />
        <Metric label="Average ticket" value={money(reports.avg_ticket || 0)} detail="Revenue ÷ bills" icon={CircleDollarSign} tone="slate" />
      </div>
      <section className="panel report-panel">
        <div className="panel-head"><div><span className="overline">REVENUE PERFORMANCE</span><h2>Sales by day (last 7 days)</h2></div></div>
        {has ? <div className="sales-bars">{sales.map((x) => (<div key={x.date}><b>{money(x.value)}</b><i style={{ height: `${(x.value / max) * 200}px` }} /><small>{x.label}</small></div>))}</div>
          : <div className="empty" data-testid="reports-empty">No sales in the last 7 days.</div>}
      </section>
      <section className="panel">
        <div className="panel-head"><div><span className="overline">PROFITABILITY MATRIX</span><h2>Best performing menu items</h2></div></div>
        {(reports.items || []).length === 0 ? <div className="empty small">No items sold yet.</div> :
        <div className="table-wrap"><table><thead><tr><th>Menu item</th><th>Units sold</th><th>Revenue</th><th>Food cost</th><th>Contribution margin</th></tr></thead><tbody>
          {reports.items.map((x) => (<tr key={x.name}><td><b>{x.name}</b></td><td>{x.sales}</td><td>{money(x.revenue)}</td><td>{money(x.cost)}</td><td>{x.margin == null ? "—" : <span className={`cost-chip ${x.margin >= 60 ? "good" : "warn"}`}>{x.margin}%</span>}</td></tr>))}
        </tbody></table></div>}
      </section>
    </>
  );
}

function Branches({ branches, current, setBranch, outlet, onAdded }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", city: "", state: "", gstin: "", fssai: "", default_gst_rate: 5, contact_phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const openCreate = () => { setEditing(null); setForm({ name: "", city: "", state: outlet?.state || "", gstin: "", fssai: "", default_gst_rate: outlet?.default_gst_rate || 5, contact_phone: "" }); setAddOpen(true); setErr(""); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, city: b.city || "", state: b.state || outlet?.state || "", gstin: b.gstin || "", fssai: b.fssai || "", default_gst_rate: b.default_gst_rate || outlet?.default_gst_rate || 5, contact_phone: b.contact_phone || "" }); setAddOpen(true); setErr(""); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
      } else {
        await api.post(`/branches`, form);
      }
      setAddOpen(false); setEditing(null); onAdded?.();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {outlet && (
        <section className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-head"><div><span className="overline">RESTAURANT PROFILE</span><h2>{outlet.display_name}</h2></div></div>
          <div className="outlet-grid">
            <div><small>Legal name</small><b>{outlet.legal_name || "—"}</b></div>
            <div><small>GSTIN</small><b>{outlet.gstin || "—"}</b></div>
            <div><small>FSSAI</small><b>{outlet.fssai || "—"}</b></div>
            <div><small>State</small><b>{outlet.state}</b></div>
            <div><small>Default GST</small><b>{outlet.default_gst_rate}%</b></div>
            <div><small>HSN / SAC</small><b>{outlet.default_hsn_sac}</b></div>
            <div><small>Channels</small><b>{(outlet.sales_channels || []).join(" · ")}</b></div>
            <div><small>Contact</small><b>{outlet.contact_phone} · {outlet.contact_email}</b></div>
            <div style={{ gridColumn: "1 / -1" }}><small>Address</small><b>{outlet.address}, {outlet.city} {outlet.pincode}</b></div>
          </div>
        </section>
      )}

      <div className="page-head" style={{ marginBottom: 18 }}>
        <div>
          <span className="overline">LOCATIONS</span>
          <h2 style={{ fontSize: 20, margin: "6px 0 2px" }}>Your branches</h2>
          <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>Each branch can keep its own GSTIN, FSSAI and state so franchise bills print the right numbers.</p>
        </div>
        <button className="primary-button" onClick={openCreate} data-testid="add-branch-button"><Plus size={16}/> Add branch</button>
      </div>

      {branches.length === 0 ? (
        <div className="panel" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }} data-testid="branches-empty">No branches yet. Add your first location to start operating.</div>
      ) : (
        <section className="branch-grid">
          {branches.map((b) => (
            <article className={current === b.id ? "branch-card selected" : "branch-card"} key={b.id} data-testid={`branch-card-${b.id}`}>
              <button type="button" className="branch-card-main" onClick={() => setBranch(b.id)}>
                <div className="branch-avatar large">{b.initials}</div>
                <div><h3>{b.name}</h3><span>{b.city || "—"}{b.gstin ? ` · ${b.gstin}` : ""}</span></div>
                {current === b.id && <span className="status success"><Check size={13} /> Active</span>}
              </button>
              <button type="button" className={b.hotel_enabled ? "primary-button" : "secondary-button"} style={{ margin: "0 6px", whiteSpace: "nowrap", flexShrink: 0 }} onClick={async () => { await api.put(`/branches/${b.id}`, { hotel_enabled: !b.hotel_enabled }); onAdded?.(); }} data-testid={`hotel-toggle-${b.id}`} title="Rooms & hall bookings for this branch"><BedDouble size={14}/> Hotel module: {b.hotel_enabled ? "On" : "Off"}</button>
              <button type="button" className="icon-button branch-edit" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`} title="Edit branch"><Pencil size={14}/></button>
            </article>
          ))}
        </section>
      )}

      {addOpen && (
        <div className="modal-backdrop">
          <div className="modal" data-testid="branch-modal">
            <div className="modal-head"><div><span className="overline">FYNDINE</span><h2>{editing ? "Edit branch" : "Add a branch"}</h2></div><button className="icon-button" onClick={() => { setAddOpen(false); setEditing(null); }} data-testid="close-branch-modal"><X size={18}/></button></div>
            <form onSubmit={save}>
              <label>Branch name<input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="e.g. FynDine Dhanbad" data-testid="branch-name-input"/></label>
              <div className="form-grid">
                <label>City / area<input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="e.g. Dhanbad, JH" data-testid="branch-city-input"/></label>
                <label>State<input value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} placeholder="State for CGST+SGST / IGST" data-testid="branch-state-input"/></label>
                <label>Branch GSTIN<input value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value.toUpperCase().slice(0,15)})} maxLength={15} placeholder="07AAACM1234F1Z5" data-testid="branch-gstin-input"/></label>
                <label>Branch FSSAI<input value={form.fssai} onChange={(e) => setForm({...form, fssai: e.target.value.replace(/\D/g,"").slice(0,14)})} maxLength={14} placeholder="12345678901234" data-testid="branch-fssai-input"/></label>
                <label>Default GST %<select value={form.default_gst_rate} onChange={(e) => setForm({...form, default_gst_rate: Number(e.target.value)})} data-testid="branch-gst-rate"><option value={0}>0%</option><option value={5}>5%</option><option value={12}>12%</option><option value={18}>18%</option><option value={28}>28%</option></select></label>
                <label>Branch phone<input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} placeholder="Enter 10-digit mobile number" data-testid="branch-phone-input"/></label>
              </div>
              <p className="form-hint">Leave GSTIN / FSSAI blank to inherit from the restaurant profile. If filled, this branch's bills will use these numbers.</p>
              {err && <div className="auth-alert error">{err}</div>}
              <button className="primary-button full" disabled={busy || !form.name.trim()} data-testid="save-branch-button"><Check size={16}/> {editing ? "Save changes" : "Create branch"}</button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function Subscription({ user, trialDays }) {
  const endDate = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const endLabel = endDate ? fmtD(endDate.toISOString()) : "—";
  const pct = Math.min(100, Math.max(0, (trialDays / 7) * 100));
  return (
    <section className="subscription-layout">
      <div className="subscription-hero">
        <span className="overline">YOUR WORKSPACE</span>
        <h2>Make every ingredient count.</h2>
        <p>Your 7-day trial gives you full access to multi-branch kitchen intelligence, recipe costing and GST-compliant billing.</p>
        <div className="trial-meter">
          <div><span>Trial period</span><b>{trialDays} days remaining</b></div>
          <div className="progress"><i style={{ width: `${pct}%` }} /></div>
          <small>Trial ends on {endLabel} (IST)</small>
        </div>
        <button className="primary-button" data-testid="choose-plan-button">Choose a plan <ArrowUpRight size={15} /></button>
      </div>
      <div className="plan-list">
        <div className="plan-card">
          <span className="category-label">RECOMMENDED</span>
          <h3>Professional</h3>
          <p>For growing restaurants with multiple locations.</p>
          <strong>₹8,999 <small>/ month</small></strong>
          <ul>
            <li><Check size={14} /> Unlimited branches</li>
            <li><Check size={14} /> Advanced recipe costing</li>
            <li><Check size={14} /> GST-compliant invoicing</li>
            <li><Check size={14} /> WhatsApp &amp; SMS bill sharing</li>
          </ul>
        </div>
        <div className="plan-card muted-plan">
          <h3>Starter</h3>
          <p>For one location getting organized.</p>
          <strong>₹3,999 <small>/ month</small></strong>
        </div>
      </div>
    </section>
  );
}
